/**
 * Mock Evidence Data for Development
 * 
 * Provides realistic test data for the Evidence Modal components.
 * Used when API is unavailable or in development mode.
 * 
 * @see auditEvidenceService.ts
 */

import { EvidenceContract, MetricKey, RunMetadata } from '@/types/evidenceContract';

// =============================================================================
// SHARED RUN METADATA (used across all metrics for same run)
// Supports Audit Framework Q2.1, Q2.2, Q2.3, Q5.1
// =============================================================================

const MOCK_RUN_METADATA: RunMetadata = {
  algorithm_version: 'verascore_v2.3',
  git_sha: '9f2c8a1b',
  config_hash: 'sha256:curve_scoring_v2.1_abc123',
  input_txn_count: 287,
  input_account_count: 3,
  input_months_coverage: 24,
  plaid_snapshot_id: 'snap-plaid-2025-12-31-001',
  crs_snapshot_id: 'snap-crs-2025-12-15-001',
  status: 'SUCCESS',
  execution_time_ms: 1247,
  run_ts: '2025-12-31T23:59:59Z',
};

// =============================================================================
// MOCK EVIDENCE CONTRACTS
// Keys match CANONICAL_METRICS from vs-integrations/api/src/services/audit/canonical-metrics.ts
// =============================================================================

export const MOCK_EVIDENCE: Record<string, EvidenceContract> = {
  // === INCOME METRICS ===

  // Monthly gross income / Money In (most commonly clicked)
  // Canonical key: money_in_1m (from CANONICAL_METRICS.MONEY_IN_1M)
  'money_in_1m': {
    run_id: 'mock-run-001',
    metric_key: 'money_in_1m' as MetricKey,
    label: 'Gross Income',
    value: 4765.08,
    unit: 'USD',
    period: { type: 'MONTH', start: '2025-12-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$4,765.08',
      one_liner: 'Sum of 8 income transactions this month',
      trend: 'STABLE',
      trend_percent: 1.2,
      coaching_tip: 'Your income is consistent with prior months.',
    },
    components: {
      count: 8,
      breakdown_type: 'CATEGORY',
      items: [
        { name: 'Salary/Wages', percentage: 89, count: 2 },
        { name: 'Freelance', percentage: 8, count: 5 },
        { name: 'Interest', percentage: 3, count: 1 },
      ],
    },
    calculation: {
      formula: 'SUM(amount) WHERE category IN (INCOME) AND date IN month',
      steps: [
        { order: 1, name: 'Filter', detail: 'Filter transactions by date range → 45 transactions' },
        { order: 2, name: 'Classify', detail: 'Classify by Plaid category → 8 income, 37 other' },
        { order: 3, name: 'Sum', detail: 'Sum income amounts → $4,765.08', intermediate_value: 4765.08 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
    sources: {
      accounts: [
        { account_id: 'acct-001', mask: '1189', institution: 'Chase', type: 'depository', balance_current: 5240.00, balance_used: 5240.00, include_reason: 'Primary checking' },
      ],
      transactions: {
        included_count: 8,
        excluded_count: 1,
        included: [
          { tx_id: 'txn-001', date: '2025-12-15', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          { tx_id: 'txn-002', date: '2025-12-01', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          { tx_id: 'txn-003', date: '2025-12-10', amount: 450.00, merchant: 'FREELANCE CLIENT', category: 'Income', direction: 'credit' },
        ],
        excluded: [
          { tx_id: 'txn-ex-001', reason: 'transfer_internal' },
        ],
      },
    },
    integrity: {
      inputs_hash: 'sha256:abc123def456789...',
      outputs_hash: 'sha256:def456abc789012...',
      verified: true,
      verification_timestamp: '2025-12-31T23:59:59Z',
    },
  },

  // 6-month rolling income (annualized)
  // Canonical key: gross_income_6m (from CANONICAL_METRICS.GROSS_INCOME_6M)
  // BUG-UI-029/062 FIX: Updated formula to show proper annualization calculation
  'gross_income_6m': {
    run_id: 'mock-run-001',
    metric_key: 'gross_income_6m' as MetricKey,
    label: 'Deposited Income (6 Mo Avg, Annualized)',
    value: 72000.00, // BUG-UI-061: Annualized value ($6,000/mo × 12)
    unit: 'USD',
    period: { type: 'ROLLING_6M', start: '2025-07-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$72,000.00',
      one_liner: 'Based on Deposited Income',
      trend: 'STABLE',
      trend_percent: 2.3,
      coaching_tip: 'Your income has been consistent. Consider diversifying income sources for stability.',
    },
    components: {
      count: 2,
      breakdown_type: 'METRIC',
      items: [
        { name: 'Total', value: 72000.00 },
        { name: 'Month Count', value: 6 },
      ],
    },
    calculation: {
      // BUG-UI-029/062 FIX: Proper annualization formula
      formula: '(SUM(monthly_deposits, 6 months) / 6) × 12',
      steps: [
        { order: 1, name: 'Data Fetch', detail: 'Retrieved transactions from database' },
        { order: 2, name: 'Classification', detail: 'Applied transaction classification rules' },
        { order: 3, name: 'Sum 6-Month', detail: 'Sum deposits over 6 months → $36,000', intermediate_value: 36000.00 },
        { order: 4, name: 'Monthly Avg', detail: 'Calculate monthly average: $36,000 / 6 = $6,000', intermediate_value: 6000.00 },
        { order: 5, name: 'Annualize', detail: 'Annualize: $6,000 × 12 = $72,000', intermediate_value: 72000.00 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9ee9ede',
        algorithm_version: 'v2.3.0',
      },
    },
    sources: {
      accounts: [
        { account_id: 'acct-001', mask: '1189', institution: 'Chase', type: 'depository', balance_current: 5240.00, balance_used: 5240.00, include_reason: 'Primary checking' },
        { account_id: 'acct-002', mask: '4521', institution: 'Chase', type: 'depository', balance_current: 12500.00, balance_used: 12500.00, include_reason: 'Savings account' },
      ],
      transactions: {
        included_count: 47,
        excluded_count: 5,
        included: [
          // December 2025 - Payroll
          { tx_id: 'txn-001', date: '2025-12-15', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          { tx_id: 'txn-002', date: '2025-12-01', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          // November 2025 - Payroll
          { tx_id: 'txn-003', date: '2025-11-15', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          { tx_id: 'txn-004', date: '2025-11-01', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          // October 2025 - Payroll
          { tx_id: 'txn-005', date: '2025-10-15', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          { tx_id: 'txn-006', date: '2025-10-01', amount: 2125.00, merchant: 'PAYROLL ACME CORP', category: 'Income', direction: 'credit' },
          // Freelance income
          { tx_id: 'txn-007', date: '2025-12-10', amount: 450.00, merchant: 'FREELANCE CLIENT A', category: 'Income', direction: 'credit' },
          { tx_id: 'txn-008', date: '2025-11-22', amount: 625.00, merchant: 'UPWORK PAYMENT', category: 'Income', direction: 'credit' },
          { tx_id: 'txn-009', date: '2025-10-28', amount: 380.00, merchant: 'FIVERR PAYMENT', category: 'Income', direction: 'credit' },
          // Interest/Dividends
          { tx_id: 'txn-010', date: '2025-12-31', amount: 45.12, merchant: 'CHASE INTEREST', category: 'Interest', direction: 'credit' },
          { tx_id: 'txn-011', date: '2025-11-30', amount: 42.88, merchant: 'CHASE INTEREST', category: 'Interest', direction: 'credit' },
          { tx_id: 'txn-012', date: '2025-10-31', amount: 41.45, merchant: 'CHASE INTEREST', category: 'Interest', direction: 'credit' },
        ],
        excluded: [
          { tx_id: 'txn-ex-001', reason: 'transfer_internal', amount: 500.00 },
          { tx_id: 'txn-ex-002', reason: 'duplicate', amount: 2125.00 },
          { tx_id: 'txn-ex-003', reason: 'reversal', amount: -450.00 },
          { tx_id: 'txn-ex-004', reason: 'transfer_internal', amount: 1000.00 },
          { tx_id: 'txn-ex-005', reason: 'credit_card_payment', amount: 350.00 },
        ],
      },
    },
    integrity: {
      inputs_hash: 'sha256:abc123def456789...',
      outputs_hash: 'sha256:def456abc789012...',
      verified: true,
      verification_timestamp: '2025-12-31T23:59:59Z',
    },
  },

  // === DTI RATIO ===
  'dti_ratio': {
    run_id: 'mock-run-001',
    metric_key: 'dti_ratio' as MetricKey,
    label: 'Debt-to-Income Ratio',
    value: 0.32,
    unit: 'RATIO',
    period: { type: 'AS_OF', as_of: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '32%',
      one_liner: 'Monthly debt payments ($1,525) / Monthly income ($4,765)',
      trend: 'DOWN',
      trend_percent: -3.2,
      coaching_tip: 'Great progress! Your DTI is in the "Good" range (<36%). Lenders typically prefer below 43%.',
    },
    components: {
      count: 8,
      breakdown_type: 'LIABILITY_TYPE',
      items: [
        { name: 'Mortgage/Rent', percentage: 52, value: 793.00 },
        { name: 'Auto Loan', percentage: 26, value: 396.50 },
        { name: 'Credit Cards', percentage: 15, value: 228.75 },
        { name: 'Student Loans', percentage: 7, value: 106.75 },
      ],
    },
    calculation: {
      formula: 'monthly_debt_payments / monthly_gross_income',
      steps: [
        { order: 1, name: 'Income', detail: 'Calculate monthly gross income → $4,765.08', intermediate_value: 4765.08 },
        { order: 2, name: 'Debt', detail: 'Sum monthly debt payments → $1,525.00', intermediate_value: 1525.00 },
        { order: 3, name: 'Ratio', detail: 'Calculate ratio → 0.32 (32%)', intermediate_value: 0.32 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
      thresholds: { excellent: 0.20, good: 0.36, fair: 0.43, poor: 0.50 },
    },
  },

  // === FACTOR SCORE CARDS ===
  // Canonical keys from CANONICAL_FACTORS

  // Canonical key: debt_to_income_factor (from CANONICAL_FACTORS.DEBT_TO_INCOME)
  'debt_to_income_factor': {
    run_id: 'mock-run-001',
    metric_key: 'debt_to_income_factor' as MetricKey,
    label: 'Debt-to-Income',
    value: 100,
    unit: 'COUNT',
    period: { type: 'MONTH', start: '2025-11-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '100/100',
      one_liner: 'Excellent DTI ratio of 5.2% (well under 36% threshold)',
      trend: 'STABLE',
      trend_percent: 0,
      coaching_tip: 'Your DTI is excellent! Lenders typically want < 36%. Yours is well below that.',
    },
    components: {
      count: 4,
      breakdown_type: 'LIABILITY_TYPE',
      items: [
        { name: 'Mortgage', percentage: 52, value: 793.00 },
        { name: 'Auto Loan', percentage: 26, value: 396.50 },
        { name: 'Credit Cards', percentage: 15, value: 228.75 },
        { name: 'Student Loans', percentage: 7, value: 106.75 },
      ],
    },
    calculation: {
      formula: 'score_from_dti_ratio(min_debt / gross_income)',
      inputs: [
        { name: 'gross_income', value: 4765.08, source: 'PLAID_TRANSACTIONS' },
        { name: 'min_debt_payments', value: 247.80, source: 'PLAID_LIABILITIES' },
      ],
      steps: [
        { order: 1, name: 'DTI Ratio', detail: 'DTI Ratio = $247.80 / $4,765.08 = 5.2%' },
        { order: 2, name: 'Score Mapping', detail: 'Score = 100 (DTI < 20% = excellent)' },
      ],
      config_ref: {
        ruleset_version: 'verascore_v2.3',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
      thresholds: { excellent: 20, good: 36, fair: 43, poor: 50 },
    },
  },

  // Canonical key: liquid_reserves_factor (from CANONICAL_FACTORS.LIQUID_RESERVES)
  'liquid_reserves_factor': {
    run_id: 'mock-run-001',
    metric_key: 'liquid_reserves_factor' as MetricKey,
    label: 'Cash on Hand',
    value: 87,
    unit: 'COUNT',
    period: { type: 'MONTH', start: '2025-11-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '87/100',
      one_liner: 'Strong liquidity with 3.7 months of expenses in reserves',
      trend: 'UP',
      trend_percent: 5.2,
      coaching_tip: 'Good cash reserves! Aim for 3-6 months of expenses for emergencies.',
    },
    components: {
      count: 3,
      breakdown_type: 'ACCOUNT',
      items: [
        { name: 'Checking', percentage: 30, value: 5240.00 },
        { name: 'Savings', percentage: 70, value: 12500.00 },
      ],
    },
    calculation: {
      formula: 'score_from_months_coverage(cash_on_hand / monthly_expenses)',
      inputs: [
        { name: 'cash_on_hand', value: 17740.00, source: 'PLAID_BALANCES' },
        { name: 'monthly_expenses', value: 4800.00, source: 'PLAID_TRANSACTIONS' },
      ],
      steps: [
        { order: 1, name: 'Months Coverage', detail: 'Months Coverage = $17,740 / $4,800 = 3.7 months' },
        { order: 2, name: 'Score Mapping', detail: 'Score = 87 (3+ months = good to excellent)' },
      ],
      config_ref: {
        ruleset_version: 'verascore_v2.3',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
  },

  // Canonical key: cash_flow_factor (from CANONICAL_FACTORS.CASH_FLOW)
  'cash_flow_factor': {
    run_id: 'mock-run-001',
    metric_key: 'cash_flow_factor' as MetricKey,
    label: 'Cash Flow / Spending',
    value: 0,
    unit: 'COUNT',
    period: { type: 'MONTH', start: '2025-11-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '0/100',
      one_liner: 'Spending exceeds income this month (negative money leftover)',
      trend: 'DOWN',
      trend_percent: -15.0,
      coaching_tip: 'This month shows more outflows than usual. Review discretionary spending.',
    },
    components: {
      count: 5,
      breakdown_type: 'CATEGORY',
      items: [
        { name: 'Housing', percentage: 35, value: 1680.00 },
        { name: 'Food & Dining', percentage: 25, value: 1200.00 },
        { name: 'Transportation', percentage: 20, value: 960.00 },
        { name: 'Utilities', percentage: 12, value: 576.00 },
        { name: 'Other', percentage: 8, value: 384.00 },
      ],
    },
  },

  // Canonical key: savings_factor (from CANONICAL_FACTORS.SAVINGS)
  'savings_factor': {
    run_id: 'mock-run-001',
    metric_key: 'savings_factor' as MetricKey,
    label: 'Savings',
    value: 0,
    unit: 'COUNT',
    period: { type: 'MONTH', start: '2025-11-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '0/100',
      one_liner: 'No net savings this month due to high expenses',
      trend: 'DOWN',
      trend_percent: -100.0,
      coaching_tip: 'Aim to save at least 10% of income. Review expenses to find opportunities.',
    },
    components: {
      count: 2,
      breakdown_type: 'TYPE',
      items: [
        { name: 'Emergency Fund', percentage: 0, value: 0 },
        { name: 'Other Savings', percentage: 0, value: 0 },
      ],
    },
  },

  // Canonical key: debt_management_factor (from CANONICAL_FACTORS.DEBT_MANAGEMENT)
  'debt_management_factor': {
    run_id: 'mock-run-001',
    metric_key: 'debt_management_factor' as MetricKey,
    label: 'Debt Management',
    value: 80,
    unit: 'COUNT',
    period: { type: 'MONTH', start: '2025-11-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '80/100',
      one_liner: 'Good debt management with decreasing balances',
      trend: 'UP',
      trend_percent: 2.5,
      coaching_tip: 'Your debt is manageable. Continue making payments above minimums when possible.',
    },
    components: {
      count: 4,
      breakdown_type: 'LIABILITY_TYPE',
      items: [
        { name: 'Mortgage', percentage: 81, value: 185000.00 },
        { name: 'Student Loans', percentage: 12, value: 28000.00 },
        { name: 'Auto Loan', percentage: 5, value: 12500.00 },
        { name: 'Credit Cards', percentage: 2, value: 3200.00 },
      ],
    },
  },

  // Canonical key: payment_history_factor (from CANONICAL_FACTORS.PAYMENT_HISTORY)
  'payment_history_factor': {
    run_id: 'mock-run-001',
    metric_key: 'payment_history_factor' as MetricKey,
    label: 'Payment History',
    value: 100,
    unit: 'COUNT',
    period: { type: 'ROLLING_12M', start: '2025-01-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '100/100',
      one_liner: 'Perfect payment history - no late payments in 12 months',
      trend: 'STABLE',
      trend_percent: 0,
      coaching_tip: 'Excellent! On-time payments are crucial for maintaining good credit.',
    },
    components: {
      count: 4,
      breakdown_type: 'ACCOUNT',
      items: [
        { name: 'Mortgage', percentage: 25, value: 12 },
        { name: 'Auto Loan', percentage: 25, value: 12 },
        { name: 'Credit Card', percentage: 25, value: 12 },
        { name: 'Student Loan', percentage: 25, value: 12 },
      ],
    },
  },

  // === LEGACY FACTOR SCORES (for backward compatibility) ===
  // These alias entries support old UI code that uses different keys
  'factor_income_stability': {
    run_id: 'mock-run-001',
    metric_key: 'factor_income_stability' as MetricKey,
    label: 'Income Stability',
    value: 85,
    unit: 'COUNT',
    period: { type: 'ROLLING_6M', start: '2025-07-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    summary: {
      display_value: '85/100',
      one_liner: 'Consistent income with low variance over 6 months',
      trend: 'STABLE',
      trend_percent: 0.5,
      coaching_tip: 'Your income stability is excellent, which is favorable for lenders.',
    },
    components: {
      count: 6,
      breakdown_type: 'MONTH',
      items: [
        { name: 'Dec 2025', percentage: 100, value: 4765.08 },
        { name: 'Nov 2025', percentage: 99, value: 4720.00 },
        { name: 'Oct 2025', percentage: 98, value: 4680.00 },
      ],
    },
  },

  'factor_expense_management': {
    run_id: 'mock-run-001',
    metric_key: 'factor_expense_management' as MetricKey,
    label: 'Expense Management',
    value: 72,
    unit: 'COUNT',
    period: { type: 'ROLLING_6M', start: '2025-07-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    summary: {
      display_value: '72/100',
      one_liner: 'Spending is within budget with room for improvement',
      trend: 'UP',
      trend_percent: 3.2,
      coaching_tip: 'Consider reducing discretionary spending to improve this score.',
    },
  },

  'factor_savings_behavior': {
    run_id: 'mock-run-001',
    metric_key: 'factor_savings_behavior' as MetricKey,
    label: 'Savings Behavior',
    value: 65,
    unit: 'COUNT',
    period: { type: 'ROLLING_6M', start: '2025-07-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    summary: {
      display_value: '65/100',
      one_liner: 'Positive savings rate but below target',
      trend: 'UP',
      trend_percent: 5.0,
      coaching_tip: 'Try to save at least 10% of income for optimal financial health.',
    },
  },

  'factor_debt_management': {
    run_id: 'mock-run-001',
    metric_key: 'factor_debt_management' as MetricKey,
    label: 'Debt Management',
    value: 78,
    unit: 'COUNT',
    period: { type: 'ROLLING_6M', start: '2025-07-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    summary: {
      display_value: '78/100',
      one_liner: 'DTI is healthy and debt is being paid down',
      trend: 'UP',
      trend_percent: 2.1,
      coaching_tip: 'Continue paying more than minimums to improve faster.',
    },
  },

  'factor_credit_history': {
    run_id: 'mock-run-001',
    metric_key: 'factor_credit_history' as MetricKey,
    label: 'Credit History',
    value: 55,
    unit: 'COUNT',
    period: { type: 'AS_OF', as_of: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    summary: {
      display_value: '55/100',
      one_liner: 'Limited credit history, some late payments',
      trend: 'STABLE',
      trend_percent: 0,
      coaching_tip: 'Maintain on-time payments to build a stronger credit history.',
    },
  },

  'factor_financial_stress': {
    run_id: 'mock-run-001',
    metric_key: 'factor_financial_stress' as MetricKey,
    label: 'Financial Stress',
    value: 68,
    unit: 'COUNT',
    period: { type: 'ROLLING_6M', start: '2025-07-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    summary: {
      display_value: '68/100',
      one_liner: 'Low stress indicators with adequate cash buffer',
      trend: 'UP',
      trend_percent: 4.5,
      coaching_tip: 'Build an emergency fund of 3-6 months expenses.',
    },
  },

  // === MINIMUM DEBT PAYMENTS ===
  // Canonical key: monthly_debt_payments (from CANONICAL_METRICS.MONTHLY_DEBT_PAYMENTS)
  'monthly_debt_payments': {
    run_id: 'mock-run-001',
    metric_key: 'monthly_debt_payments' as MetricKey,
    label: 'Minimum Debt Payments',
    value: 1525.00,
    unit: 'USD',
    period: { type: 'MONTH', start: '2025-12-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$1,525.00',
      one_liner: 'Sum of minimum required payments across 4 liability accounts',
      trend: 'DOWN',
      trend_percent: -1.5,
      coaching_tip: 'Your debt payments are decreasing. Keep making consistent payments.',
    },
    components: {
      count: 4,
      breakdown_type: 'LIABILITY_TYPE',
      items: [
        { name: 'Mortgage', percentage: 52, value: 793.00 },
        { name: 'Auto Loan', percentage: 26, value: 396.50 },
        { name: 'Credit Cards', percentage: 15, value: 228.75 },
        { name: 'Student Loans', percentage: 7, value: 106.75 },
      ],
    },
    calculation: {
      formula: 'SUM(minimum_payment) WHERE account_type IN (LIABILITY)',
      steps: [
        { order: 1, name: 'Fetch', detail: 'Get liability accounts from Plaid → 4 accounts' },
        { order: 2, name: 'Sum', detail: 'Sum minimum payments → $1,525.00', intermediate_value: 1525.00 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
    sources: {
      accounts: [
        { account_id: 'liab-001', mask: '4521', institution: 'Chase', type: 'loan', balance_current: 185000.00, balance_used: 793.00, include_reason: 'Mortgage' },
        { account_id: 'liab-002', mask: '7832', institution: 'Capital One', type: 'loan', balance_current: 12500.00, balance_used: 396.50, include_reason: 'Auto Loan' },
        { account_id: 'liab-003', mask: '9012', institution: 'Chase', type: 'credit', balance_current: 3200.00, balance_used: 228.75, include_reason: 'Credit Card' },
        { account_id: 'liab-004', mask: '3456', institution: 'Navient', type: 'loan', balance_current: 28000.00, balance_used: 106.75, include_reason: 'Student Loan' },
      ],
    },
  },

  // === CASH ON HAND ===
  // Canonical key: cash_on_hand (from CANONICAL_METRICS.CASH_ON_HAND)
  'cash_on_hand': {
    run_id: 'mock-run-001',
    metric_key: 'cash_on_hand' as MetricKey,
    label: 'Cash on Hand',
    value: 17740.00,
    unit: 'USD',
    period: { type: 'AS_OF', as_of: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$17,740.00',
      one_liner: 'Sum of balances across 3 depository accounts',
      trend: 'UP',
      trend_percent: 3.2,
      coaching_tip: 'Good liquidity! This represents about 3.7 months of expenses.',
    },
    components: {
      count: 3,
      breakdown_type: 'ACCOUNT',
      items: [
        { name: 'Savings Account', percentage: 70, value: 12500.00 },
        { name: 'Checking Account', percentage: 30, value: 5240.00 },
      ],
    },
    calculation: {
      formula: 'SUM(current_balance) WHERE account_type = DEPOSITORY',
      steps: [
        { order: 1, name: 'Fetch', detail: 'Get depository accounts from Plaid → 3 accounts' },
        { order: 2, name: 'Sum', detail: 'Sum current balances → $17,740.00', intermediate_value: 17740.00 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
    sources: {
      accounts: [
        { account_id: 'acct-001', mask: '1189', institution: 'Chase', type: 'depository', balance_current: 5240.00, balance_used: 5240.00, include_reason: 'Primary checking' },
        { account_id: 'acct-002', mask: '4521', institution: 'Chase', type: 'depository', balance_current: 12500.00, balance_used: 12500.00, include_reason: 'Savings account' },
      ],
    },
  },

  // === MONEY LEFTOVER ===
  // Canonical key: money_leftover_1m (from CANONICAL_METRICS.MONEY_LEFTOVER_1M)
  'money_leftover_1m': {
    run_id: 'mock-run-001',
    metric_key: 'money_leftover_1m' as MetricKey,
    label: 'Money Leftover',
    value: 1165.08,
    unit: 'USD',
    period: { type: 'MONTH', start: '2025-12-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$1,165.08',
      one_liner: 'Money in ($5,215) - Expenses ($2,525) - Debt payments ($1,525)',
      trend: 'UP',
      trend_percent: 8.2,
      coaching_tip: 'Positive cash flow! Consider directing surplus to savings or debt paydown.',
    },
    calculation: {
      formula: 'money_in - monthly_expenses - minimum_debt_payments',
      steps: [
        { order: 1, name: 'Money In', detail: 'Get monthly inflows → $5,215.08', intermediate_value: 5215.08 },
        { order: 2, name: 'Expenses', detail: 'Get monthly expenses → $2,525.00', intermediate_value: 2525.00 },
        { order: 3, name: 'Debt', detail: 'Get debt payments → $1,525.00', intermediate_value: 1525.00 },
        { order: 4, name: 'Subtract', detail: 'Calculate remainder → $1,165.08', intermediate_value: 1165.08 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
  },

  // === MONTHLY EXPENSES ===
  // Canonical key: money_out_1m (from CANONICAL_METRICS.MONEY_OUT_1M)
  'money_out_1m': {
    run_id: 'mock-run-001',
    metric_key: 'money_out_1m' as MetricKey,
    label: 'Monthly Expenses',
    value: 2525.00,
    unit: 'USD',
    period: { type: 'MONTH', start: '2025-12-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$2,525.00',
      one_liner: 'Sum of outflows classified as expenses (excludes debt payments)',
      trend: 'STABLE',
      trend_percent: -0.5,
      coaching_tip: 'Your spending is consistent. Look for areas to reduce if building savings.',
    },
    components: {
      count: 28,
      breakdown_type: 'CATEGORY',
      items: [
        { name: 'Food & Dining', percentage: 32, value: 808.00 },
        { name: 'Transportation', percentage: 22, value: 555.50 },
        { name: 'Utilities', percentage: 18, value: 454.50 },
        { name: 'Shopping', percentage: 15, value: 378.75 },
        { name: 'Entertainment', percentage: 8, value: 202.00 },
        { name: 'Other', percentage: 5, value: 126.25 },
      ],
    },
    calculation: {
      formula: 'SUM(amount) WHERE category IN (EXPENSE) AND date IN month',
      steps: [
        { order: 1, name: 'Filter', detail: 'Filter transactions by date → 45 transactions' },
        { order: 2, name: 'Classify', detail: 'Classify as expenses → 28 transactions' },
        { order: 3, name: 'Sum', detail: 'Sum expense amounts → $2,525.00', intermediate_value: 2525.00 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
    sources: {
      accounts: [
        { account_id: 'acct-001', mask: '1189', institution: 'Chase', type: 'depository', balance_current: 5240.00, balance_used: 5240.00, include_reason: 'Primary checking' },
      ],
      transactions: {
        included_count: 28,
        excluded_count: 5,
        included: [
          { tx_id: 'txn-e01', date: '2025-12-28', amount: -125.50, merchant: 'WHOLE FOODS', category: 'Groceries', direction: 'debit' },
          { tx_id: 'txn-e02', date: '2025-12-25', amount: -89.00, merchant: 'SHELL GAS', category: 'Transportation', direction: 'debit' },
          { tx_id: 'txn-e03', date: '2025-12-20', amount: -145.00, merchant: 'ELECTRIC COMPANY', category: 'Utilities', direction: 'debit' },
        ],
        excluded: [
          { tx_id: 'txn-ex-001', reason: 'transfer_internal' },
          { tx_id: 'txn-ex-002', reason: 'debt_payment' },
        ],
      },
    },
  },

  // === NET SAVINGS ===
  // Canonical key: net_savings_1m (from CANONICAL_METRICS.NET_SAVINGS_1M)
  'net_savings_1m': {
    run_id: 'mock-run-001',
    metric_key: 'net_savings_1m' as MetricKey,
    label: 'Net Savings',
    value: 500.00,
    unit: 'USD',
    period: { type: 'MONTH', start: '2025-12-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$500.00',
      one_liner: 'Amount transferred to savings accounts this month',
      trend: 'UP',
      trend_percent: 25.0,
      coaching_tip: 'Great job saving! Aim for 10-20% of income for optimal financial health.',
    },
    calculation: {
      formula: 'MAX(0, money_leftover - discretionary_spending)',
      steps: [
        { order: 1, name: 'Leftover', detail: 'Get money leftover → $1,165.08', intermediate_value: 1165.08 },
        { order: 2, name: 'Discretionary', detail: 'Subtract discretionary → $665.08', intermediate_value: 665.08 },
        { order: 3, name: 'Floor', detail: 'Apply floor at 0 → $500.00', intermediate_value: 500.00 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
  },

  // === OUTSTANDING DEBT ===
  // Canonical key: total_debt (from CANONICAL_METRICS.TOTAL_DEBT)
  'total_debt': {
    run_id: 'mock-run-001',
    metric_key: 'total_debt' as MetricKey,
    label: 'Outstanding Debt',
    value: 228700.00,
    unit: 'USD',
    period: { type: 'AS_OF', as_of: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '$228,700.00',
      one_liner: 'Total current balances across 4 liability accounts',
      trend: 'DOWN',
      trend_percent: -0.8,
      coaching_tip: 'Your debt is decreasing. Continue making payments above minimums when possible.',
    },
    components: {
      count: 4,
      breakdown_type: 'LIABILITY_TYPE',
      items: [
        { name: 'Mortgage', percentage: 81, value: 185000.00 },
        { name: 'Student Loans', percentage: 12, value: 28000.00 },
        { name: 'Auto Loan', percentage: 5, value: 12500.00 },
        { name: 'Credit Cards', percentage: 2, value: 3200.00 },
      ],
    },
    calculation: {
      formula: 'SUM(current_balance) WHERE account_type IN (LIABILITY)',
      steps: [
        { order: 1, name: 'Fetch', detail: 'Get liability accounts from Plaid → 4 accounts' },
        { order: 2, name: 'Sum', detail: 'Sum current balances → $228,700.00', intermediate_value: 228700.00 },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
    sources: {
      accounts: [
        { account_id: 'liab-001', mask: '4521', institution: 'Chase', type: 'loan', balance_current: 185000.00, balance_used: 185000.00, include_reason: 'Mortgage' },
        { account_id: 'liab-002', mask: '7832', institution: 'Capital One', type: 'loan', balance_current: 12500.00, balance_used: 12500.00, include_reason: 'Auto Loan' },
        { account_id: 'liab-003', mask: '9012', institution: 'Chase', type: 'credit', balance_current: 3200.00, balance_used: 3200.00, include_reason: 'Credit Card' },
        { account_id: 'liab-004', mask: '3456', institution: 'Navient', type: 'loan', balance_current: 28000.00, balance_used: 28000.00, include_reason: 'Student Loan' },
      ],
    },
  },

  // === LATE PAYMENTS ===
  // Canonical key: late_payments (from CANONICAL_METRICS.LATE_PAYMENTS)
  'late_payments': {
    run_id: 'mock-run-001',
    metric_key: 'late_payments' as MetricKey,
    label: 'Late Payments',
    value: 0,
    unit: 'COUNT',
    period: { type: 'ROLLING_12M', start: '2025-01-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '0',
      one_liner: 'No late payments reported in the last 12 months',
      trend: 'STABLE',
      trend_percent: 0,
      coaching_tip: 'Excellent payment history! Keep it up to maintain your credit score.',
    },
    components: {
      count: 0,
      breakdown_type: 'DELINQUENCY',
      items: [],
    },
    calculation: {
      formula: 'COUNT(*) WHERE delinquency_status IN (30, 60, 90, 120+) AND date >= 12_months_ago',
      steps: [
        { order: 1, name: 'Fetch', detail: 'Get payment history from bureau/Plaid → 48 payments' },
        { order: 2, name: 'Filter', detail: 'Filter for delinquencies → 0 late payments' },
      ],
      config_ref: {
        ruleset_version: 'tx_rules_v3.2',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
    sources: {
      accounts: [
        { account_id: 'liab-001', mask: '4521', institution: 'Chase', type: 'loan', balance_current: 185000.00, balance_used: 185000.00, include_reason: 'Mortgage - 0 late' },
        { account_id: 'liab-002', mask: '7832', institution: 'Capital One', type: 'loan', balance_current: 12500.00, balance_used: 12500.00, include_reason: 'Auto Loan - 0 late' },
      ],
      bureau: {
        bureau_report_id: 'EXP-2025-12-15-001',
        pull_timestamp: '2025-12-15T10:30:00Z',
        score_model: 'FICO',
        score_model_version: 'Score 8',
        tradeline_count: 12,
      },
    },
  },

  // === VERASCORE METRICS ===

  // Current VeraScore (composite score)
  'verascore_current': {
    run_id: 'mock-run-001',
    metric_key: 'verascore_current' as MetricKey,
    label: 'VeraScore',
    value: 67,
    unit: 'COUNT',
    period: { type: 'MONTH', start: '2025-11-01', end: '2025-12-31' },
    as_of: '2025-12-31T23:59:59Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '67',
      one_liner: 'Weighted combination of 6 financial health factors',
      trend: 'UP',
      trend_percent: 3.5,
      coaching_tip: 'Your VeraScore is in the Good range (60-79). Continue your positive financial habits.',
    },
    components: {
      count: 6,
      breakdown_type: 'FACTOR',
      // BUG-UI-040 FIX: Renamed "Cash on Hand" to "Liquid Reserves" for consistency
      items: [
        { name: 'Debt-to-Income', percentage: 20, value: 100 },
        { name: 'Payment History', percentage: 20, value: 100 },
        { name: 'Liquid Reserves', percentage: 20, value: 87 },
        { name: 'Debt Management', percentage: 15, value: 80 },
        { name: 'Spending', percentage: 15, value: 0 },
        { name: 'Savings', percentage: 10, value: 0 },
      ],
    },
    calculation: {
      formula: 'Σ(factor_score × weight) for 6 factors',
      inputs: [
        { name: 'factor_dti', value: 100, source: 'INTERNAL_DERIVED' },
        { name: 'factor_payment_history', value: 100, source: 'INTERNAL_DERIVED' },
        { name: 'factor_cash_on_hand', value: 87, source: 'INTERNAL_DERIVED' },
        { name: 'factor_debt', value: 80, source: 'INTERNAL_DERIVED' },
        { name: 'factor_spending', value: 0, source: 'INTERNAL_DERIVED' },
        { name: 'factor_savings', value: 0, source: 'INTERNAL_DERIVED' },
      ],
      steps: [
        { order: 1, name: 'DTI Factor', detail: 'DTI: 100 × 0.20 = 20.0 pts' },
        { order: 2, name: 'Payment History Factor', detail: 'Payment History: 100 × 0.20 = 20.0 pts' },
        { order: 3, name: 'Liquid Reserves Factor', detail: 'Liquid Reserves: 87 × 0.20 = 17.4 pts' },
        { order: 4, name: 'Debt Management Factor', detail: 'Debt Management: 80 × 0.15 = 12.0 pts' },
        { order: 5, name: 'Spending Factor', detail: 'Spending: 0 × 0.15 = 0.0 pts' },
        { order: 6, name: 'Savings Factor', detail: 'Savings: 0 × 0.10 = 0.0 pts' },
        { order: 7, name: 'Total', detail: 'Total: 20.0 + 20.0 + 17.4 + 12.0 + 0.0 + 0.0 = 69.4 → 67 (rounded)' },
      ],
      config_ref: {
        ruleset_version: 'verascore_v2.3',
        git_sha: '9f2c8a1',
        algorithm_version: 'verascore_v2.3',
      },
    },
    integrity: {
      inputs_hash: 'sha256:vs_inputs_123...',
      outputs_hash: 'sha256:vs_outputs_456...',
      verified: true,
      verification_timestamp: '2025-12-31T23:59:59Z',
    },
  },

  // === CREDIT SCORE (External Bureau Data) ===
  // Canonical key: credit_score (uses simpler key)
  // BUG-UI-056: Updated to show Equifax as source (our CRS provider)
  'credit_score': {
    run_id: 'mock-run-001',
    metric_key: 'credit_score' as MetricKey,
    label: 'Credit Score',
    value: 620,
    unit: 'COUNT',
    period: { type: 'AS_OF', as_of: '2025-12-15' },
    as_of: '2025-12-15T10:30:00Z',
    run_metadata: MOCK_RUN_METADATA,
    summary: {
      display_value: '620',
      one_liner: 'External credit data sourced from Equifax via Credit Reporting Service (CRS).',
      trend: 'UP',
      trend_percent: 5,
      coaching_tip: 'Your score is in the Fair range (580-669). On-time payments and reducing credit utilization can help improve it.',
    },
    sources: {
      accounts: [
        {
          account_id: 'bureau-001',
          mask: 'N/A',
          institution: 'Equifax',
          type: 'credit' as const,
          balance_current: 0,
          balance_used: 0,
          include_reason: 'Primary credit bureau source via CRS'
        },
      ],
      bureau: {
        bureau_report_id: 'EQX-2025-12-15-001',
        pull_timestamp: '2025-12-15T10:30:00Z',
        score_model: 'FICO',
        score_model_version: 'Score 8',
        tradeline_count: 12,
      },
    },
    integrity: {
      inputs_hash: 'sha256:bureau123...',
      outputs_hash: 'sha256:bureau456...',
      verified: true,
      verification_timestamp: '2025-12-15T10:30:00Z',
    },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get mock evidence for a specific metric
 */
export function getMockEvidence(metricKey: string): EvidenceContract | null {
  return MOCK_EVIDENCE[metricKey] || null;
}

/**
 * Check if mock data should be FORCED (bypasses API entirely)
 *
 * By default, we try the API first and fall back to mock data on failure.
 * Set VITE_USE_MOCK_EVIDENCE=true to force mock data (useful for offline dev).
 *
 * Changed Jan 5, 2026: Removed `import.meta.env.DEV` check so API is tried in dev mode.
 */
export function shouldUseMockData(): boolean {
  return import.meta.env.VITE_USE_MOCK_EVIDENCE === 'true';
}

